import React, { useState } from 'react';
import { MapPin, Phone, Mail, ArrowRight, CheckCircle } from 'lucide-react';

import { useTranslation } from "react-i18next";
import { functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

const ContactPage = () => {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const sendEmail = httpsCallable(functions, 'sendContactEmail');
      await sendEmail(formData);

      setIsSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
      toast.success(t("contact.successTitle"));

      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error(error.message || t("common.error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-fecos-bg min-h-screen text-fecos-text pb-24">

      {/* 1. Page Header (Minimal) */}
      <section className="bg-fecos-bg pt-24 pb-12 overflow-hidden border-b border-fecos-border/50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <span className="text-fecos-accent font-bold text-xs tracking-[0.2em] uppercase mb-4 block animate-in fade-in slide-in-from-bottom-2">
            {t("contact.headerTitle")}
          </span>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-fecos-text animate-in fade-in slide-in-from-bottom-3">
            {t("contact.headerSubtitle")}
          </h1>
          <p className="text-fecos-gray text-lg font-medium max-w-xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom-4">
            {t("contact.headerText")}
          </p>
        </div>
      </section>

      <div className="max-w-[1240px] mx-auto px-6 lg:px-12 mt-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">

          {/* --- LEFT: Contact Form --- */}
          <div className="bg-fecos-surface rounded-[32px] p-8 lg:p-12 shadow-sm border border-fecos-border/40">
            {isSuccess ? (
              <div className="h-full flex flex-col items-center justify-center text-center min-h-[400px]">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center text-green-600 mb-6 font-bold">
                  <CheckCircle size={32} />
                </div>
                <h3 className="text-2xl font-bold text-fecos-text mb-2">{t("contact.successTitle")}</h3>
                <p className="text-fecos-gray max-w-xs mx-auto">
                  {t("contact.successText")}
                </p>
                <button onClick={() => setIsSuccess(false)} className="mt-6 text-sm font-bold text-fecos-text underline">{t("contact.sendAnother")}</button>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold mb-8 text-fecos-text">{t("contact.formTitle")}</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-fecos-text/60 uppercase tracking-wider">{t("contact.name")}</label>
                      <input type="text" name="name" value={formData.name} onChange={handleChange} required
                        className="w-full bg-fecos-bg border border-transparent focus:bg-white focus:border-fecos-text/20 rounded-xl px-4 py-3.5 text-sm font-medium outline-none transition-all placeholder:text-gray-400" placeholder={t("contact.name")} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-fecos-text/60 uppercase tracking-wider">{t("contact.email")}</label>
                      <input type="email" name="email" value={formData.email} onChange={handleChange} required
                        className="w-full bg-fecos-bg border border-transparent focus:bg-white focus:border-fecos-text/20 rounded-xl px-4 py-3.5 text-sm font-medium outline-none transition-all placeholder:text-gray-400" placeholder={t("contact.email")} />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-fecos-text/60 uppercase tracking-wider">{t("contact.subject")}</label>
                    <select name="subject" value={formData.subject} onChange={handleChange}
                      className="w-full bg-fecos-bg border border-transparent focus:bg-white focus:border-fecos-text/20 rounded-xl px-4 py-3.5 text-sm font-medium outline-none transition-all text-fecos-text appearance-none cursor-pointer">
                      <option value="">{t("contact.subjects.default")}</option>
                      <option value="order">{t("contact.subjects.order")}</option>
                      <option value="product">{t("contact.subjects.product")}</option>
                      <option value="wholesale">{t("contact.subjects.wholesale")}</option>
                      <option value="other">{t("contact.subjects.other")}</option>
                    </select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-fecos-text/60 uppercase tracking-wider">{t("contact.message")}</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} required rows="5"
                      className="w-full bg-fecos-bg border border-transparent focus:bg-white focus:border-fecos-text/20 rounded-xl px-4 py-3.5 text-sm font-medium outline-none transition-all placeholder:text-gray-400 resize-none" placeholder={t("contact.message")}></textarea>
                  </div>

                  <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-fecos-text text-white rounded-xl font-bold text-sm tracking-widest uppercase hover:bg-black transition-all flex items-center justify-center gap-2">
                    {isSubmitting ? t("contact.sending") : t("contact.send")}
                    {!isSubmitting && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* --- RIGHT: Info --- */}
          <div className="space-y-8 flex flex-col justify-center">

            <div>
              <h3 className="text-2xl font-bold mb-6 text-fecos-text">{t("contact.infoTitle")}</h3>
              <p className="text-fecos-gray text-lg leading-relaxed mb-8">
                {t("contact.infoText")}
              </p>
            </div>

            <div className="space-y-5">
              <div className="flex items-center gap-5 p-6 bg-fecos-surface rounded-2xl shadow-sm border border-fecos-border/40 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-fecos-bg rounded-full flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5 text-fecos-text" />
                </div>
                <div>
                  <div className="text-xs font-bold text-fecos-gray uppercase">{t("contact.phoneLabel")}</div>
                  <div className="text-base font-semibold text-fecos-text mt-0.5">+90 (539) 282 96 96</div>
                </div>
              </div>

              <div className="flex items-center gap-5 p-6 bg-fecos-surface rounded-2xl shadow-sm border border-fecos-border/40 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-fecos-bg rounded-full flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5 text-fecos-text" />
                </div>
                <div>
                  <div className="text-xs font-bold text-fecos-gray uppercase">{t("contact.emailLabel")}</div>
                  <div className="text-base font-semibold text-fecos-text mt-0.5">support@fecosfood.com</div>
                </div>
              </div>

              <div className="flex items-center gap-5 p-6 bg-fecos-surface rounded-2xl shadow-sm border border-fecos-border/40 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-fecos-bg rounded-full flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5 text-fecos-text" />
                </div>
                <div>
                  <div className="text-xs font-bold text-fecos-gray uppercase">{t("contact.hqLabel")}</div>
                  <div className="text-base font-semibold text-fecos-text mt-0.5">
                    {t("contact.address")}
                  </div>
                </div>
              </div>
            </div>

          </div>

        </div>

        {/* --- BOTTOM: MAP --- */}
        <div className="mt-16 rounded-[32px] overflow-hidden shadow-lg border border-gray-200 bg-fecos-surface p-2 h-[400px]">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d203875.52157152062!2d35.16335967073286!3d37.014168057285516!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x15288f38a79c7193%3A0xd61674d85803b9c9!2sAdana%2C%20Turkey!5e0!3m2!1sen!2str!4v1715456123456!5m2!1sen!2str"
            width="100%"
            height="100%"
            style={{ border: 0, borderRadius: '24px', filter: 'grayscale(0.5) contrast(1)' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="FecosFood Location"
          ></iframe>
        </div>

      </div>
    </div>
  );
};

export default ContactPage;